import { useTimeTracker } from '@/hooks/useTimeTracker';
import { formatTimestamp, calculateMinutes, formatDurationJa } from '@/lib/utils';
import { Link } from 'react-router-dom';

export const RecentRecordsCard = () => {
  const { recentRecords, isLoading } = useTimeTracker();

  if (isLoading) {
    return (
      <div className="card animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  // 直近3件のみ表示
  const displayedRecords = recentRecords.slice(0, 3);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">最近の稼働履歴</h2>
        <Link 
          to="/summary#records"
          className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          すべて表示 →
        </Link>
      </div>

      {recentRecords.length === 0 ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <p className="text-gray-500">稼働履歴はありません</p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedRecords.map((record) => {
            // serverTimestamp() が null の場合をスキップ
            if (!record.clockInTime) {
              return null;
            }

            return (
            <div
              key={record.id}
              className="group p-4 rounded-xl border-2 border-gray-100 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Project Info */}
                  <div className="flex items-center gap-2 mb-2">
                    {record.status === 'active' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                        出勤中
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        完了
                      </span>
                    )}
                    
                    <h3 className="text-sm font-semibold text-gray-900">
                      {record.project?.name || '不明なプロジェクト'}
                    </h3>
                  </div>

                  {/* Time Info */}
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      <span>{formatTimestamp(record.clockInTime)}</span>
                    </div>

                    {record.clockOutTime && (
                      <>
                        <span className="text-gray-400">→</span>
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>{formatTimestamp(record.clockOutTime)}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Memo */}
                  {record.notes && (
                    <p className="mt-2 text-sm text-gray-600 line-clamp-1">
                      {record.notes}
                    </p>
                  )}
                </div>

                {/* Duration */}
                {record.clockInTime && record.clockOutTime && (
                  <div className="ml-4 text-right">
                    <div className="text-xs text-gray-500 mb-1">稼働時間</div>
                    <div className="text-lg font-bold text-primary-600 tabular-nums">
                      {formatDurationJa(calculateMinutes(record.clockInTime, record.clockOutTime))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
